<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onUnmounted } from "vue";

import type { LanguageListItem } from "@/types/config";

import { configStore } from "@/stores/config";

import {
  getCurrentLocale,
  getValueByLang,
  hasDefaultCountryLocale,
  isDefaultCountryLocale
} from "@/mixins/translatedProperty";

import Dialog from "@/components/elements/Dialog.vue";

const props = defineProps({
  langCode: {
    type: String,
    default: '',
  },
  translations: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["setLang", "deleteText"]);

const config = configStore();
const openDropdown = ref(false);
const allLanguages = config.$state.list.languages as LanguageListItem[];
const currentLocale = getCurrentLocale()
const langToDelete = ref('');

const elDropdown = ref();
const dialogDelete = ref();

const dropdownStyles = reactive<Record<string, string | undefined>>({
  position: "fixed",
  top: undefined,
  bottom: undefined,
  left: undefined,
  right: undefined
});

watch(openDropdown, () => {
  updateDropdownPosition();
});

const availableLanguages = computed(() => {
  return allLanguages.filter(l => (hasTranslation(l.code) || l.code === currentLocale));
});

function getLangCode(code: string) {
  if (hasDefaultCountryLocale(code) && isDefaultCountryLocale(code)) {
    return code.substring(0, 2);
  }
  return code.replace('_', ' - ');
}

function setLang(code: string) {
  emit("setLang", code || 'en_US');
  openDropdown.value = false;
}

function hasTranslation(code: string) {
  const value = getValueByLang(props.translations, code, true).value;
  return typeof value != 'undefined' && value.length > 0 ;
}

function updateDropdownPosition() {
  if (!elDropdown.value) return;

  const parentRect = elDropdown.value.getBoundingClientRect();

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const right = viewportWidth - parentRect.right;

  const spaceBelow = viewportHeight - parentRect.bottom;
  const spaceAbove = parentRect.top;

  dropdownStyles.top = undefined;
  dropdownStyles.bottom = undefined;
  dropdownStyles.left = undefined;
  dropdownStyles.right = undefined;

  dropdownStyles.right = `${right}px`;

  if (spaceBelow >= 360) {
    dropdownStyles.top = `${parentRect.bottom}px`;
  } else if (spaceAbove >= 360) {
    dropdownStyles.bottom = `${viewportHeight - parentRect.top}px`;
  } else {
    const middleY = viewportHeight / 2;
    const dropdownHeight = 360;

    let topPos = middleY - dropdownHeight / 2;
    if (topPos < 0) topPos = 0;
    if (topPos + dropdownHeight > viewportHeight) topPos = viewportHeight - dropdownHeight;

    dropdownStyles.top = `${topPos}px`;
  }
}

function handleResize() {
  if (openDropdown.value) {
    updateDropdownPosition();
  }
}

function startDeleteContent(lang: string) {
  langToDelete.value = lang;
  dialogDelete.value.open();
}

function deleteContent() {
  const newLocale = getFallbackLocale(langToDelete.value);

  emit('deleteText', langToDelete.value);
  openDropdown.value = false;
  langToDelete.value = '';
  emit("setLang", newLocale);
}

function getFallbackLocale(locale: string) {
  const shortLocale = locale.substring(0, 2);
  const availableTranslations = JSON.parse(JSON.stringify(props.translations));
  if (availableTranslations[locale]) {
    delete availableTranslations[locale];
  } else if (isDefaultCountryLocale(locale) && availableTranslations[shortLocale]) {
    delete availableTranslations[shortLocale];
  }

  return getValueByLang(availableTranslations, locale).lang || 'en_US';
}

onMounted(() => {
  updateDropdownPosition();
  window.addEventListener("resize", handleResize);
})

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

</script>
<template>
	<div ref="elDropdown" class="language-dropdown">
		<button
      @click="openDropdown = true"
      :disabled="availableLanguages.length < 2"
      class="button language-dropdown__trigger">
      <i v-if="hasTranslation(langCode)" class="fa-thin fa-check"></i>
			{{ getLangCode(langCode || 'en_US') }}
		</button>
    <Teleport to="body">
      <Transition name="opacity">
        <div v-show="openDropdown" class="language-dropdown__background" @click="openDropdown = false"></div>
      </Transition>
      <Transition name="grow">
        <div
          v-if="availableLanguages.length > 1"
          v-show="openDropdown"
          :style="dropdownStyles"
          class="language-dropdown__container">
          <div class="language-dropdown__container__body">
            <ul class="language-dropdown__container__list">
              <li
                v-for="lang in availableLanguages"
                @click="setLang(lang.code)"
                class="language-dropdown__container__list__item"
                :class="{'language-dropdown__container__list__item--system-lang': currentLocale === lang.code }">
                <span class="language-dropdown__container__list__item__indicator">
                  <i
                    v-if="hasTranslation(lang.code)"
                    class="fa-thin fa-check">
                  </i>
                  {{ getLangCode(lang.code) }}
                </span>
                <span class="language-dropdown__container__list__item__label">
                  {{ lang.name }}
                </span>
                <button
                  v-if="hasTranslation(lang.code) && Object.keys(translations).length > 1"
                  @click.stop="startDeleteContent(lang.code)"
                  class="button button--blank button--blank--focus button--icon">
                  <i class="fa-light fa-trash"></i>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </Transition>
    </Teleport>
	</div>
  
  <Dialog
    ref="dialogDelete"
    @submit="deleteContent"
    :title="$t('language_text.popup_delete.title')"
    :text="$t('language_text.popup_delete.question')"
    :submit-text="$t('ui.delete')"
    :cancel-text="$t('ui.cancel')"
  />
</template>
